import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PRISM_PRODUCT } from './websiteData';

const CPG_LAYERS = [
  { abbr: 'AST', name: 'Abstract Syntax Tree', color: '#06b6d4', desc: '58 universal NodeKind constants. Every supported language maps to the same structural representation.' },
  { abbr: 'CFG', name: 'Control Flow Graph', color: '#0891b2', desc: 'Per-function basic blocks with true/false/next/exit edges. Dead-code detection and reachability analysis.' },
  { abbr: 'DDG', name: 'Data Dependency Graph', color: '#0e7490', desc: 'Reaching-definitions analysis. Every variable definition mapped to every use. Source-to-sink taint tracking.' },
  { abbr: 'CDG', name: 'Control Dependency Graph', color: '#155e75', desc: 'Predicate → controlled-statement edges via Lengauer-Tarjan. Handles goto, labeled breaks, closures.' },
  { abbr: 'CG', name: 'Call Graph', color: '#164e63', desc: 'Caller → callee edges resolved from the AST. Drives inter-procedural taint propagation across function boundaries.' },
];

const LANGUAGES = [
  { name: 'Java', icon: '☕', status: 'stable', rules: 31, note: '31 rules. Spring annotations, IFDS context-sensitive analysis, reflection tracking, lambda/stream lifting. Scanned Spring Framework (3.7M CPG nodes).' },
  { name: 'Go', icon: '🐹', status: 'stable', rules: 23, note: '23 rules. Full inter-procedural + field-sensitive taint. CHA for interface resolution. Goroutine leak detection.' },
  { name: 'Python', icon: '🐍', status: 'stable', rules: 24, note: '24 rules. Flask/Django/FastAPI framework support. Pickle/marshal/YAML deserialization. CSRF, bare-except quality checks.' },
  { name: 'Kotlin', icon: '🟣', status: 'stable', rules: 24, note: '24 rules. Extension functions, coroutines. Shares Java taint infrastructure with Kotlin-specific patterns.' },
  { name: 'JavaScript', icon: '🌐', status: 'stable', rules: 22, note: '22 rules. Express/Koa framework sources/sinks. Prototype pollution. Promise-no-catch quality. Console-log detection.' },
  { name: 'TypeScript', icon: '🔷', status: 'stable', rules: 16, note: '16 rules. Full parser + CPG + taint. Type parameter tracking. Shared JS ecosystem rules.' },
  { name: 'ABAP', icon: '🏭', status: 'stable', rules: 20, note: '20 rules. First open-source SAST. Dynamic SQL injection, AUTHORITY-CHECK bypass, RFC exposure. Pure Go parser.' },
  { name: 'COBOL', icon: '🏛️', status: 'stable', rules: 15, note: '15 rules + modernization scoring. Copybook inlining. REDEFINES alias tracking. EXEC CICS/SQL analysis.' },
  { name: 'JCL', icon: '📋', status: 'stable', rules: 4, note: '4 rules. Job Control Language for z/OS. Dataset naming, job structure analysis.' },
];

const COMPARISON = [
  { metric: 'Cold start', prism: '38ms ✅', semgrep: '2–8s', joern: '8–15s', codeql: '10–60s' },
  { metric: 'Binary size', prism: '26MB ✅', semgrep: '~80MB', joern: '~200MB', codeql: '~400MB' },
  { metric: 'Runtime deps', prism: 'None ✅', semgrep: 'Python', joern: 'JVM', codeql: 'JVM + CLI' },
  { metric: 'Memory (50K LOC)', prism: '42MB ✅', semgrep: '~300MB', joern: '~800MB', codeql: '~2GB' },
  { metric: 'Languages', prism: '9 ✅', semgrep: '35+', joern: '15+', codeql: '20+' },
  { metric: 'Built-in rules', prism: '179 ✅', semgrep: '2000+', joern: '~50', codeql: '~300' },
  { metric: 'Taint analysis', prism: 'Inter + field ✅', semgrep: 'Intra only', joern: 'Full ✅', codeql: 'Full ✅' },
  { metric: 'Field-sensitive taint', prism: '✅', semgrep: '❌', joern: '✅', codeql: 'Partial' },
  { metric: 'Studio (10 analyses)', prism: '✅', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'Risk quadrants', prism: '✅ 4 dimensions', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'Function complexity', prism: 'CC per function ✅', semgrep: '❌', joern: 'Partial', codeql: 'Partial' },
  { metric: 'Temporal coupling', prism: '✅ git+CPG', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'Knowledge / Bus factor', prism: '✅ per file', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'Architectural health', prism: '✅ instability', semgrep: '❌', joern: 'Partial', codeql: '❌' },
  { metric: 'Refactoring ROI', prism: '✅ hrs/yr', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'Blast radius', prism: '✅ BFS callgraph', semgrep: '❌', joern: '❌', codeql: 'Partial' },
  { metric: 'CI regression gate', prism: '✅ baseline diff', semgrep: 'Partial', joern: '❌', codeql: 'Partial' },
  { metric: 'Code PageRank', prism: '✅', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'PROVE assertions', prism: '✅ NEVER/ALWAYS', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'Quantifiers', prism: '✅ FORALL/EXISTS', semgrep: '❌', joern: 'Partial', codeql: '❌' },
  { metric: 'ABAP support', prism: '✅ Native', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'COBOL support', prism: '✅ Native', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'Embeddable as lib', prism: 'Go pkg ✅', semgrep: '❌', joern: '❌', codeql: '❌' },
  { metric: 'Open source', prism: 'MIT ✅', semgrep: 'LGPL', joern: 'Apache 2', codeql: 'Proprietary' },
];

const COMPARISON_CATEGORIES = [
  {
    category: 'Pattern-matching scanners',
    icon: '🔍',
    gap: 'Find syntax patterns. Miss data flows, ignore churn, skip architecture.',
    prism: 'Full CPG with taint tracking across function boundaries. Every finding weighted by churn, bus factor, and call-graph centrality.',
  },
  {
    category: 'Academic query engines',
    icon: '🎓',
    gap: 'Expressive query languages that require dedicated training. Minutes to cold start. Gigabytes of RAM.',
    prism: 'PQL reads like English. 38ms cold start. 42MB peak memory. Quantifiers (FORALL/EXISTS) for formal reasoning without the ceremony.',
  },
  {
    category: 'Enterprise SAST suites',
    icon: '🏢',
    gap: 'Enterprise complexity to manage enterprise complexity. JVM runtimes. Licence fees per developer. Separate tools for each concern.',
    prism: '26MB binary. MIT licensed. Studio runs 10 analyses in parallel. No JVM. No licence. No tool sprawl.',
  },
  {
    category: 'Code quality platforms',
    icon: '📊',
    gap: 'Measure code smells and coverage. No security taint. No risk weighting. No knowledge dimension.',
    prism: 'Health, risk, architecture, knowledge, and security in one graph. Risk quadrants combine all dimensions into a prioritised action list.',
  },
  {
    category: 'Legacy language scanners',
    icon: '🏛️',
    gap: 'ABAP and COBOL support is proprietary, expensive, and JVM-based. SAP tooling licences required.',
    prism: 'Pure Go parsers for ABAP, COBOL, and JCL. Copybook inlining. REDEFINES alias tracking. Modernization scoring. No JVM. No SAP licence.',
  },
];

const CODE_INTEL_CARDS = [
  { icon: '🔍', title: 'X-Ray', desc: 'Cyclomatic complexity scored per function — not per file. Combined with git churn and PageRank to surface the exact functions eating your time.' },
  { icon: '🏗️', title: 'Architecture', desc: 'Robert Martin instability index per module. Circular dependency detection. God module flags. Health graded A–F. Decay visible before it compounds.' },
  { icon: '👥', title: 'Knowledge', desc: 'Bus factor per file: minimum authors covering 50% of lines. Orphaned file detection. One departure should not make a module unmaintainable.' },
  { icon: '📈', title: 'Trends', desc: 'Per-file trajectory over 90 days. Rising means churn is accelerating and the file is growing. Improving means net code deletion. Track direction, not just state.' },
  { icon: '🔀', title: 'Coupling', desc: 'File pairs that co-change in git history but share zero structural edges in the CPG. Hidden dependencies that only surface when something breaks in production.' },
  { icon: '💰', title: 'ROI', desc: 'Complexity debt × annual churn → dev-hours saved per year. A function with CC 47 touched 60 times a year costs roughly 30 hours in avoidable friction. Now you can see it.' },
];

const DECISION_MAKERS = [
  { big: '5%', desc: "of your functions carry 80% of your risk. PRISM's PageRank tells you which ones." },
  { big: 'Bus factor: 1', desc: 'means one resignation away from unmaintainable. Knowledge maps show where.' },
  { big: '40 hrs/year', desc: 'evaporating into one over-complex function. ROI scoring makes the business case.' },
  { big: 'EU CRA Art. 20', desc: 'requires source-level security evidence. PRISM generates it from the CPG.' },
];

const DEEP_DIVE_TABS = [
  {
    id: 'arena',
    label: 'Arena Architecture',
    content: 'Flat contiguous slices: Nodes []NodeData, Edges []EdgeData, Adj [][]uint32. Four GC-visible slice headers regardless of graph size. NodeData = 52 bytes. EdgeData = 12 bytes. String interning eliminates duplicate file paths and identifiers. The result: sub-millisecond GC pauses even at 3.7 million nodes.',
  },
  {
    id: 'taint',
    label: 'Taint Engine',
    content: 'Inter-procedural, field-sensitive taint analysis. Worklist-based GEN/KILL with alias expansion. Function summaries enable cross-boundary tracking without re-analysis. COBOL REDEFINES aliases handled natively. Struct-field granularity: obj.Username (tainted) does not contaminate obj.IsAdmin (clean).',
  },
  {
    id: 'pql',
    label: 'PQL Grammar',
    content: 'FIND function WHERE complexity > 15 AND callers.count > 5 RETURN name, file, pagerank ORDER BY pagerank DESC LIMIT 10. Quantifiers: FORALL (every path must satisfy) and EXISTS (at least one path). PROVE NEVER/ALWAYS for formal security assertions. Same find clause in both rule mode (PRL) and query mode (PQL).',
  },
  {
    id: 'prove',
    label: 'PROVE Assertions',
    content: 'PROVE NEVER: assert that no execution path allows a condition. PROVE ALWAYS: assert that every path through a function satisfies a guard. Failed assertions produce counterexample traces with exact CPG node references. Use in CI to enforce architectural invariants — not just catch bugs.',
  },
];

const STUDIO_ANALYSES = [
  { icon: '🔍', name: 'Scan', desc: 'Security & quality findings with full taint paths' },
  { icon: '❤️', name: 'Health', desc: 'Per-package score 0–100, grade A–F' },
  { icon: '🔥', name: 'Risk', desc: 'File risk matrix across 4 quadrants' },
  { icon: '⚡', name: 'X-Ray', desc: 'Cyclomatic complexity per function' },
  { icon: '🏗️', name: 'Arch', desc: 'Module instability, cycles, god modules' },
  { icon: '👥', name: 'Authors', desc: 'Knowledge map & bus factor per file' },
  { icon: '📈', name: 'Trends', desc: 'Rising / stable / improving over 90 days' },
  { icon: '📦', name: 'SBOM', desc: 'CycloneDX / SPDX bill of materials' },
  { icon: '🔀', name: 'Coupling', desc: 'Implicit temporal dependencies in git' },
  { icon: '💰', name: 'ROI', desc: 'Complexity debt × churn = refactoring priority' },
];

const STUDIO_DERIVATIONS = [
  { icon: '👻', title: 'Ghost Logic', desc: 'Complexity outliers that are rarely touched — high complexity, low churn. Forgotten danger zones that nobody maintains but everyone depends on.' },
  { icon: '🌉', title: 'Bridges', desc: 'Fragile architectural articulation points: files that connect major modules. Delete one bridge file and entire subsystems disconnect.' },
  { icon: '🛡️', title: 'Missing Guards', desc: "Security patterns absent where they're expected: HTTP handlers without auth, database calls without sanitisation, file operations without path validation." },
];

const RISK_QUADRANTS = [
  { icon: '🚨', label: 'CRITICAL', color: '#ef4444', bgColor: 'rgba(239,68,68,.08)', borderColor: 'rgba(239,68,68,.25)', xLabel: 'High Churn', yLabel: 'High Security Risk', desc: 'Fix immediately. These files are touched constantly and are riddled with vulnerabilities. Every deployment is a lottery.' },
  { icon: '⚠️', label: 'LATENT', color: '#f59e0b', bgColor: 'rgba(245,158,11,.08)', borderColor: 'rgba(245,158,11,.25)', xLabel: 'Low Churn', yLabel: 'High Security Risk', desc: 'Time bombs. High security pressure, rarely touched — until a junior dev adds a feature and everything breaks.' },
  { icon: '👁', label: 'MONITOR', color: '#3b82f6', bgColor: 'rgba(59,130,246,.08)', borderColor: 'rgba(59,130,246,.25)', xLabel: 'High Churn', yLabel: 'Low Security Risk', desc: 'Under surveillance. High change rate but currently clean — watch for regressions as pressure increases.' },
  { icon: '✓', label: 'HEALTHY', color: '#10b981', bgColor: 'rgba(16,185,129,.08)', borderColor: 'rgba(16,185,129,.25)', xLabel: 'Low Churn', yLabel: 'Low Security Risk', desc: 'No action needed. Stable, clean, and infrequently modified. The reference for what well-maintained code looks like.' },
];

const ALL_COMMANDS = [
  { cmd: 'scan', purpose: 'Security & quality findings', highlight: '--hotspot  --diff-from  --save-baseline' },
  { cmd: 'health', purpose: 'Per-package 0–100 health score', highlight: '--format json|html|table' },
  { cmd: 'risk', purpose: 'File risk matrix (4 quadrants)', highlight: '--mode security|quality|all' },
  { cmd: 'xray', purpose: 'Function cyclomatic complexity', highlight: '--top N  --min-complexity' },
  { cmd: 'arch', purpose: 'Architectural instability & cycles', highlight: '--format  --exclude-tests' },
  { cmd: 'authors', purpose: 'Knowledge map & bus factor', highlight: '--format  --exclude-tests' },
  { cmd: 'trends', purpose: 'Rising/stable/improving files', highlight: '--since-days N  --format' },
  { cmd: 'coupling', purpose: 'Implicit temporal dependencies', highlight: '--min N  --format' },
  { cmd: 'roi', purpose: 'Refactoring ROI prioritisation', highlight: '--format json|html|table' },
  { cmd: 'impact', purpose: 'Blast radius for changed files', highlight: '--changed  --since  --format' },
  { cmd: 'gate', purpose: 'CI regression gate', highlight: '--baseline  --max-new N  --save' },
  { cmd: 'query', purpose: 'Interactive PQL queries', highlight: '--interactive  --file  --max-files' },
  { cmd: 'cpg', purpose: 'CPG export & inspection', highlight: '--format json|dot' },
  { cmd: 'sbom', purpose: 'SBOM generation (CycloneDX/SPDX)', highlight: '--format  --out file' },
  { cmd: 'nous', purpose: 'AI agent (graph-grounded)', highlight: 'Multi-provider: Copilot / OpenAI / Anthropic' },
  { cmd: 'modernize', purpose: 'COBOL migration risk scoring', highlight: '--format json|table' },
  { cmd: 'serve', purpose: 'Playground or Studio dashboard', highlight: '--studio  --port N  --host' },
  { cmd: 'rule', purpose: 'Rule inspection & listing', highlight: 'list  explain  --all' },
];

export default function WebsitePrismPage() {
  const { icon, accentColor, accentRgb, tagline, heroTitle, heroLead, features, roadmap, prlExample, abapExample, vrlExample } = PRISM_PRODUCT;
  const [activeTab, setActiveTab] = useState('arena');

  return (
    <div className="dgw-page-wrap">
      {/* Hero */}
      <section style={{
        padding: '80px clamp(20px,5vw,60px) 60px',
        background: '#04080f',
        borderBottom: '1px solid rgba(255,255,255,.08)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position:'absolute', top:'-80px', left:'50%', transform:'translateX(-50%)',
          width:700, height:500, borderRadius:'50%',
          background:`radial-gradient(circle, rgba(${accentRgb},.12), transparent 70%)`,
          pointerEvents:'none',
        }} />
        <div style={{maxWidth:1280, margin:'0 auto', position:'relative', zIndex:1, textAlign:'center'}}>
          <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:24, justifyContent:'center'}}>
            <span style={{fontSize:40}}>{icon}</span>
            <span style={{
              fontSize:12, fontWeight:700, letterSpacing:'.8px', textTransform:'uppercase',
              color: accentColor,
              background:`rgba(${accentRgb},.1)`,
              border:`1px solid rgba(${accentRgb},.25)`,
              padding:'5px 14px', borderRadius:100,
            }}>{tagline}</span>
          </div>
          <h1 style={{fontSize:'clamp(40px,6vw,72px)', fontWeight:800, color:'#fff', letterSpacing:'-2px', lineHeight:1.05, marginBottom:20}}>
            {heroTitle}
          </h1>
          <p style={{fontSize:18, color:'#94a3b8', lineHeight:1.7, maxWidth:660, margin:'0 auto 32px'}}>{heroLead}</p>

          {/* Quick stats bar */}
          <div style={{
            display:'flex', gap:0, justifyContent:'center', flexWrap:'wrap',
            background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)',
            borderRadius:14, padding:'16px 32px',
            maxWidth:780, margin:'0 auto 36px',
          }}>
            {[
              { val: '179', label: 'rules' },
              { val: '9', label: 'languages' },
              { val: '18', label: 'commands' },
              { val: '26MB', label: 'binary' },
              { val: '38ms', label: 'cold start' },
            ].map((s, i) => (
              <div key={s.label} style={{
                flex:1, minWidth:100, textAlign:'center',
                padding:'0 20px',
                borderLeft: i > 0 ? '1px solid rgba(255,255,255,.08)' : 'none',
              }}>
                <div style={{fontSize:22, fontWeight:800, color: accentColor, letterSpacing:'-1px'}}>{s.val}</div>
                <div style={{fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:'.6px', marginTop:2}}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap'}}>
            <Link to="/trial?plan=professional&product=prism" className="dgw-btn dgw-btn-prism dgw-btn-lg">Explore PRISM →</Link>
            <Link to="/website/product/vigil" className="dgw-btn dgw-btn-ghost dgw-btn-lg">See Vigil →</Link>
            <Link to="/website/product/depguard" className="dgw-btn dgw-btn-ghost dgw-btn-lg">See DepGuard →</Link>
          </div>
        </div>
      </section>

      <div style={{maxWidth:1280, margin:'0 auto', padding:'80px clamp(20px,5vw,60px)'}}>

        {/* What PRISM sees that others miss */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Beyond scanning</span>
            <h2 style={{color:'var(--dgw-text)'}}>What PRISM sees that others miss</h2>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:24}}>
            <div className="dgw-card" style={{padding:'32px 28px'}}>
              <h4 style={{color:'var(--dgw-text)', fontSize:18, fontWeight:800, marginBottom:12}}>The hidden architecture</h4>
              <p style={{color:'var(--dgw-muted)', fontSize:15, lineHeight:1.7, margin:0}}>
                Files that always break together — but share zero structural links. Modules where one person's departure means nobody can maintain the code. Circular dependencies silently accumulating. PRISM makes this visible.
              </p>
            </div>
            <div className="dgw-card" style={{padding:'32px 28px'}}>
              <h4 style={{color:'var(--dgw-text)', fontSize:18, fontWeight:800, marginBottom:12}}>The real priority</h4>
              <p style={{color:'var(--dgw-muted)', fontSize:15, lineHeight:1.7, margin:0}}>
                500 findings and no ranking is the same as zero findings. PRISM scores every finding across four dimensions: security severity, git churn frequency, knowledge concentration, and call-graph centrality. The result is a priority list, not a pile.
              </p>
            </div>
            <div className="dgw-card" style={{padding:'32px 28px'}}>
              <h4 style={{color:'var(--dgw-text)', fontSize:18, fontWeight:800, marginBottom:12}}>The cost of complexity</h4>
              <p style={{color:'var(--dgw-muted)', fontSize:15, lineHeight:1.7, margin:0}}>
                That function with cyclomatic complexity 47 costs roughly 30 dev-hours a year in avoidable friction. PRISM calculates the actual refactoring ROI — hours saved per year if you simplify it. Engineering time has a price. Now you can measure it.
              </p>
            </div>
          </div>
        </div>

        {/* What makes PRISM different */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Anti-patterns</span>
            <h2 style={{color:'var(--dgw-text)'}}>What makes PRISM different</h2>
            <p className="dgw-section-lead">
              Most tools solve one concern per binary. PRISM builds a single Code Property Graph and runs 18 different analyses against it.
              No duplicate parsing. No tool sprawl. No diverging results.
            </p>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20}}>
            <div style={{padding:'28px 24px', borderRadius:16, background:'rgba(239,68,68,.05)', border:'1px solid rgba(239,68,68,.15)'}}>
              <div style={{fontSize:13, fontWeight:800, color:'#ef4444', textTransform:'uppercase', letterSpacing:'.6px', marginBottom:12}}>The old way</div>
              <p style={{color:'var(--dgw-muted)', fontSize:14, lineHeight:1.7, margin:0}}>
                SAST scanner, SCA scanner, license checker, quality tool, architecture checker — five tools, five parse steps, five dashboards. Each knows nothing about the others.
              </p>
            </div>
            <div style={{padding:'28px 24px', borderRadius:16, background:`rgba(${accentRgb},.05)`, border:`1px solid rgba(${accentRgb},.2)`}}>
              <div style={{fontSize:13, fontWeight:800, color:accentColor, textTransform:'uppercase', letterSpacing:'.6px', marginBottom:12}}>PRISM</div>
              <p style={{color:'var(--dgw-muted)', fontSize:14, lineHeight:1.7, margin:0}}>
                One parse. One graph. Security, architecture, knowledge, trends, coupling, ROI — all answered from the same CPG. Studio runs all 10 analyses in parallel and streams results live.
              </p>
            </div>
            <div style={{padding:'28px 24px', borderRadius:16, background:'rgba(16,185,129,.05)', border:'1px solid rgba(16,185,129,.15)'}}>
              <div style={{fontSize:13, fontWeight:800, color:'#10b981', textTransform:'uppercase', letterSpacing:'.6px', marginBottom:12}}>The result</div>
              <p style={{color:'var(--dgw-muted)', fontSize:14, lineHeight:1.7, margin:0}}>
                26MB binary. 38ms cold start. 42MB peak memory. Zero runtime dependencies. MIT licensed. Runs in CI, on a laptop, or embedded as a Go package inside your own tools.
              </p>
            </div>
          </div>
        </div>

        {/* CPG layers */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Architecture</span>
            <h2 style={{color:'var(--dgw-text)'}}>Five graph layers. One flat arena.</h2>
            <p className="dgw-section-lead">
              58 universal node kinds. 12 edge types. 4 GC-visible slice headers regardless of graph size.
              NodeData is 52 bytes. Sub-millisecond GC pauses at 3.7 million nodes.
            </p>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:12}}>
            {CPG_LAYERS.map((layer, i) => (
              <div key={layer.abbr} className="dgw-cpg-layer-row" style={{
                padding:'20px 24px', borderRadius:14,
                background:'rgba(6,182,212,.04)',
                border:`1px solid rgba(6,182,212,.${12 - i*2})`,
                transition:'all .2s',
              }}>
                <div style={{
                  width:56, height:56, borderRadius:12,
                  background: layer.color,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:13, fontWeight:800, color:'#fff', letterSpacing:'.5px',
                }}>{layer.abbr}</div>
                <div style={{fontSize:15, fontWeight:700, color:'var(--dgw-text)'}}>{layer.name}</div>
                <div style={{fontSize:14, color:'var(--dgw-muted)', lineHeight:1.5}}>{layer.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* PRISM Studio */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Studio</span>
            <h2 style={{color:'var(--dgw-text)'}}>10 analyses. One scan. One dashboard.</h2>
            <p className="dgw-section-lead">
              <code style={{color:accentColor, background:`rgba(${accentRgb},.1)`, padding:'2px 7px', borderRadius:4, fontFamily:'monospace', fontSize:13}}>prism serve --studio</code>{' '}
              runs all 10 intelligence analyses in parallel and streams results into a live web dashboard.
              Not a sequence of separate commands — a single scan that gives you the complete picture.
            </p>
          </div>

          {/* 10 analyses grid */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:48}}>
            {STUDIO_ANALYSES.map((a) => (
              <div key={a.name} style={{
                padding:'18px 16px', borderRadius:12, textAlign:'center',
                background:`rgba(${accentRgb},.04)`,
                border:`1px solid rgba(${accentRgb},.15)`,
                transition:'border-color .2s',
              }}>
                <div style={{fontSize:24, marginBottom:8}}>{a.icon}</div>
                <div style={{fontSize:13, fontWeight:800, color:'var(--dgw-text)', marginBottom:4, textTransform:'uppercase', letterSpacing:'.4px'}}>{a.name}</div>
                <div style={{fontSize:12, color:'var(--dgw-muted)', lineHeight:1.5}}>{a.desc}</div>
              </div>
            ))}
          </div>

          {/* Phase B derivations */}
          <div style={{
            padding:'32px clamp(20px,4vw,48px)',
            borderRadius:16,
            background:'rgba(255,255,255,.025)',
            border:'1px solid rgba(255,255,255,.08)',
            marginBottom:32,
          }}>
            <div style={{fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'.7px', color:'#64748b', marginBottom:20}}>
              Phase B — Post-analysis derivations
            </div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:24}}>
              {STUDIO_DERIVATIONS.map(d => (
                <div key={d.title} style={{display:'flex', gap:14, alignItems:'flex-start'}}>
                  <span style={{fontSize:22, flexShrink:0, marginTop:2}}>{d.icon}</span>
                  <div>
                    <div style={{fontSize:14, fontWeight:700, color:'var(--dgw-text)', marginBottom:4}}>{d.title}</div>
                    <div style={{fontSize:13, color:'var(--dgw-muted)', lineHeight:1.6}}>{d.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Terminal command */}
          <div style={{
            background:'#0d1117', borderRadius:12, overflow:'hidden',
            border:'1px solid rgba(255,255,255,.08)',
            maxWidth:600, margin:'0 auto',
          }}>
            <div style={{
              display:'flex', alignItems:'center', gap:8, padding:'8px 14px',
              background:'#161b22', borderBottom:'1px solid rgba(255,255,255,.07)',
            }}>
              {['#ef4444','#f59e0b','#10b981'].map(c => <div key={c} style={{width:8,height:8,borderRadius:'50%',background:c}} />)}
              <span style={{fontSize:11, color:'#4a607a', marginLeft:4, fontFamily:'monospace'}}>terminal</span>
            </div>
            <div style={{padding:'16px 20px', fontFamily:'monospace', fontSize:13, lineHeight:1.8}}>
              <span style={{color:'#4a607a'}}>$ </span>
              <span style={{color:'#e6edf3'}}>prism serve --studio --port 4242</span>
              <br />
              <span style={{color:'#3fb950'}}>✓ Studio ready</span>
              <span style={{color:'#8b949e'}}> → http://localhost:4242</span>
              <br />
              <span style={{color:'#8b949e'}}>  179 rules loaded · 9 languages</span>
              <br />
              <span style={{color:'#8b949e'}}>  scan       </span><span style={{color:'#3fb950'}}>done</span><span style={{color:'#8b949e'}}> 247ms</span>
              <br />
              <span style={{color:'#8b949e'}}>  xray       </span><span style={{color:'#3fb950'}}>done</span><span style={{color:'#8b949e'}}> 2.1s</span>
              <br />
              <span style={{color:'#f0883e'}}>  ghost_logic </span><span style={{color:'#f0883e'}}>3 outliers detected</span>
            </div>
          </div>
        </div>

        {/* Code Intelligence */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Code intelligence</span>
            <h2 style={{color:'var(--dgw-text)'}}>Six dimensions. One graph. No guesswork.</h2>
            <p className="dgw-section-lead">
              Security scanners stop at vulnerabilities. PRISM measures the structural properties that determine
              how much damage those vulnerabilities can actually cause — and what it costs to fix them.
            </p>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:20}}>
            {CODE_INTEL_CARDS.map(c => (
              <div key={c.title} style={{
                padding:'28px 24px', borderRadius:14,
                background:`rgba(${accentRgb},.04)`,
                border:`1px solid rgba(${accentRgb},.15)`,
              }}>
                <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
                  <span style={{fontSize:24}}>{c.icon}</span>
                  <span style={{fontSize:16, fontWeight:800, color:'var(--dgw-text)'}}>{c.title}</span>
                </div>
                <p style={{fontSize:14, color:'var(--dgw-muted)', lineHeight:1.7, margin:0}}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Quadrants */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:32}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Live demo</span>
            <h2 style={{color:'var(--dgw-text)'}}>See the CPG being built</h2>
            <p className="dgw-section-lead">
              Watch PRISM build the graph step by step — from raw source bytes to a confirmed
              SQL injection finding. 10 interactive steps. Click any node. Toggle graph layers.
            </p>
          </div>

          <div style={{
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 8px 64px rgba(6,182,212,.18), 0 0 0 1px rgba(6,182,212,.25)',
            background: '#090c14',
            maxWidth: 1100,
            margin: '0 auto',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 18px',
              background: '#111827',
              borderBottom: '1px solid rgba(255,255,255,.07)',
            }}>
              <div style={{display:'flex', gap:6}}>
                {['#ef4444','#f59e0b','#10b981'].map(c => (
                  <div key={c} style={{width:10,height:10,borderRadius:'50%',background:c}} />
                ))}
              </div>
              <span style={{
                fontFamily:"'SF Mono','Fira Code',monospace", fontSize:11,
                color:'#4a607a', letterSpacing:'0.5px',
              }}>
                PRISM — Unified Code Property Graph · Interactive visualization
              </span>
              <a
                href="/prism-cpg-demo.html"
                target="_blank"
                rel="noreferrer"
                style={{
                  marginLeft:'auto', fontSize:11, color:'#6366f1',
                  textDecoration:'none', fontWeight:600,
                  padding:'3px 10px', borderRadius:5,
                  border:'1px solid rgba(99,102,241,.3)',
                  background:'rgba(99,102,241,.08)',
                }}
              >
                Open full screen ↗
              </a>
            </div>

            <iframe
              src="/prism-cpg-demo.html"
              title="PRISM Code Property Graph — Interactive Demo"
              style={{
                width: '100%',
                height: 640,
                border: 'none',
                display: 'block',
              }}
              loading="lazy"
            />
          </div>

          <p style={{textAlign:'center', marginTop:18, fontSize:13, color:'var(--dgw-muted)'}}>
            Use <kbd style={{background:'#1e293b',color:'#94a3b8',border:'1px solid #334155',borderRadius:4,padding:'1px 6px',fontFamily:'monospace'}}>← →</kbd> or{' '}
            <kbd style={{background:'#1e293b',color:'#94a3b8',border:'1px solid #334155',borderRadius:4,padding:'1px 6px',fontFamily:'monospace'}}>Space</kbd>{' '}
            to navigate · click any graph node to inspect it · toggle AST / CFG / DDG / CDG layers
          </p>
        </div>

        {/* Languages */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Languages</span>
            <h2 style={{color:'var(--dgw-text)'}}>9 languages. One engine. One rule format.</h2>
            <p className="dgw-section-lead">
              Every language shares the same 58-node universal AST, the same taint engine,
              and the same PRL rule syntax. 179 rules total. Adding a new language takes days, not months.
            </p>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16}}>
            {LANGUAGES.map(lang => (
              <div key={lang.name} style={{
                padding:'20px 22px', borderRadius:14,
                background: 'rgba(6,182,212,.06)',
                border: '1px solid rgba(6,182,212,.25)',
              }}>
                <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10}}>
                  <span style={{fontSize:24}}>{lang.icon}</span>
                  <span style={{fontSize:16, fontWeight:700, color:'var(--dgw-text)'}}>{lang.name}</span>
                  <span style={{
                    marginLeft:'auto', fontSize:10, fontWeight:700,
                    padding:'2px 8px', borderRadius:20,
                    background: 'rgba(16,185,129,.15)',
                    color: '#10b981',
                    border: '1px solid rgba(16,185,129,.3)',
                    textTransform:'uppercase', letterSpacing:'.5px',
                  }}>{lang.status}</span>
                </div>
                <div style={{fontSize:12, color: accentColor, fontWeight:600, marginBottom:6}}>
                  {lang.rules} built-in rules
                </div>
                <div style={{fontSize:13, color:'var(--dgw-muted)', lineHeight:1.5}}>{lang.note}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ABAP showcase — industry-first differentiator */}
        <div style={{
          marginBottom:100,
          padding:'48px clamp(24px,5vw,64px)',
          borderRadius:20,
          background:'linear-gradient(135deg, rgba(6,182,212,.07) 0%, rgba(6,182,212,.03) 100%)',
          border:'1px solid rgba(6,182,212,.2)',
        }}>
          <div className="dgw-2col" style={{gap:48, alignItems:'start'}}>
            {/* Left: ABAP story */}
            <div>
              <span style={{
                display:'inline-block', marginBottom:16,
                fontSize:11, fontWeight:700, letterSpacing:'.8px', textTransform:'uppercase',
                color: accentColor, background:`rgba(${accentRgb},.1)`,
                border:`1px solid rgba(${accentRgb},.25)`, padding:'4px 12px', borderRadius:100,
              }}>ABAP security</span>
              <h2 style={{fontSize:'clamp(24px,3vw,36px)', fontWeight:800, color:'var(--dgw-text)', marginBottom:16, lineHeight:1.15}}>
                Open-source SAST for SAP ABAP
              </h2>
              <p style={{color:'var(--dgw-muted)', fontSize:15, lineHeight:1.7, marginBottom:24}}>
                SAP ABAP runs the critical business logic of <strong style={{color:'var(--dgw-text)'}}>99 of the world's top 100 companies</strong>.
                An ABAP SQL injection doesn't just leak data — it exposes every salary,
                every purchase order, every customer record in the organisation.
              </p>
              <p style={{color:'var(--dgw-muted)', fontSize:15, lineHeight:1.7, marginBottom:28}}>
                PRISM ships a <strong style={{color:'var(--dgw-text)'}}>native ABAP parser</strong> — no JVM,
                no SAP tooling licence, no tree-sitter dependency. Just a single Go binary
                that understands ABAP's unique syntax, including multi-word keywords,
                FORM/METHOD/CLASS scoping, and dynamic SQL patterns.
              </p>
              <div style={{display:'flex', flexDirection:'column', gap:12}}>
                {[
                  { icon:'🛡️', text:'Dynamic SQL injection via WHERE (<var>) patterns' },
                  { icon:'🔐', text:'Missing AUTHORITY-CHECK before database reads' },
                  { icon:'📡', text:'RFC exposure with user-controlled DESTINATION' },
                ].map(item => (
                  <div key={item.text} style={{display:'flex', gap:12, alignItems:'flex-start'}}>
                    <span style={{fontSize:18, flexShrink:0, marginTop:1}}>{item.icon}</span>
                    <span style={{fontSize:14, color:'var(--dgw-muted)', lineHeight:1.5}}>{item.text}</span>
                  </div>
                ))}
              </div>
              <div style={{marginTop:24, padding:'12px 16px', borderRadius:10, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.07)'}}>
                <div style={{fontSize:11, color:'#64748b', marginBottom:4, textTransform:'uppercase', letterSpacing:'.5px'}}>vs alternatives</div>
                <div style={{fontSize:13, color:'var(--dgw-muted)', lineHeight:1.6}}>
                  Enterprise ABAP scanners are proprietary, expensive, and JVM-based.
                  PRISM is <strong style={{color:'var(--dgw-text)'}}>MIT licensed, zero-dependency, and runs in your CI in seconds</strong>.
                </div>
              </div>
            </div>
            {/* Right: ABAP rule example */}
            <div>
              <div style={{fontSize:13, fontWeight:700, color:'var(--dgw-muted)', marginBottom:8, letterSpacing:'.4px', textTransform:'uppercase'}}>
                ABAP Security Rule (PRL)
              </div>
              <div className="dgw-code-block">
                <div className="dgw-code-block-header">
                  <span>abap/dynamic-sql.prism</span>
                  <span style={{color:'#22d3ee'}}>SAP ABAP</span>
                </div>
                <pre style={{fontSize:12, lineHeight:1.65}}>{abapExample}</pre>
              </div>
              <div style={{marginTop:12, fontSize:13, color:'var(--dgw-muted)', lineHeight:1.6}}>
                The same PRL syntax used for Go rules. One rule format, all languages.
                15 unit tests + 7 integration tests, 0 false positives on safe handlers.
              </div>
            </div>
          </div>
        </div>

        {/* Features grid */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Capabilities</span>
            <h2 style={{color:'var(--dgw-text)'}}>179 rules. 18 commands. 26MB.</h2>
          </div>
          <div className="dgw-feature-grid">
            {features.map(f => (
              <div key={f.title} className="dgw-feature-item">
                <div className="dgw-feature-item-icon">{f.icon}</div>
                <div className="dgw-feature-item-title">{f.title}</div>
                <div className="dgw-feature-item-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Quadrants */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Risk intelligence</span>
            <h2 style={{color:'var(--dgw-text)'}}>Not all findings are equal. PRISM knows which ones matter.</h2>
            <p className="dgw-section-lead">
              Every file lands in one of four quadrants. Security pressure × git churn × knowledge risk.
              CRITICAL means fix it now. LATENT means it will bite you on the next release. The quadrant
              determines urgency — not the severity label alone.
            </p>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:16}}>
            {RISK_QUADRANTS.map(q => (
              <div key={q.label} style={{
                padding:'28px 24px', borderRadius:16,
                background: q.bgColor,
                border:`1px solid ${q.borderColor}`,
              }}>
                <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12}}>
                  <span style={{fontSize:24}}>{q.icon}</span>
                  <span style={{fontSize:13, fontWeight:900, color: q.color, textTransform:'uppercase', letterSpacing:'1px'}}>{q.label}</span>
                </div>
                <div style={{display:'flex', gap:8, marginBottom:14, flexWrap:'wrap'}}>
                  <span style={{fontSize:11, background:`rgba(255,255,255,.06)`, border:'1px solid rgba(255,255,255,.1)', borderRadius:20, padding:'2px 8px', color:'#94a3b8'}}>{q.xLabel}</span>
                  <span style={{fontSize:11, background:`rgba(255,255,255,.06)`, border:'1px solid rgba(255,255,255,.1)', borderRadius:20, padding:'2px 8px', color:'#94a3b8'}}>{q.yLabel}</span>
                </div>
                <p style={{fontSize:14, color:'var(--dgw-muted)', lineHeight:1.65, margin:0}}>{q.desc}</p>
              </div>
            ))}
          </div>
          <div style={{marginTop:24, textAlign:'center', fontSize:13, color:'var(--dgw-muted)'}}>
            Run <code style={{color:accentColor, background:`rgba(${accentRgb},.1)`, padding:'1px 6px', borderRadius:4, fontFamily:'monospace'}}>prism risk ./src --mode all --format html</code> to get the full quadrant report.
          </div>
        </div>

        {/* Rule examples */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Rule language</span>
            <h2 style={{color:'var(--dgw-text)'}}>One DSL. Two modes. All languages.</h2>
            <p className="dgw-section-lead">
              PRL for rule-based CI/CD scanning.
              PQL for interactive CPG exploration. Same <code style={{color: accentColor, background:'rgba(6,182,212,.1)', padding:'1px 5px', borderRadius:4, fontSize:13}}>find</code> clause — promote
              an interactive query to a rule file with zero rewrite.
            </p>
          </div>
          <div className="dgw-code-pair-grid" style={{gap:24, marginBottom:24}}>
            <div>
              <div style={{fontSize:13, fontWeight:700, color:'var(--dgw-muted)', marginBottom:8, letterSpacing:'.4px', textTransform:'uppercase'}}>PRL Rule — Go SQL injection</div>
              <div className="dgw-code-block">
                <div className="dgw-code-block-header">
                  <span>go/sqli.prism</span>
                  <span style={{color:'#22d3ee'}}>PRISM Rule Language</span>
                </div>
                <pre style={{fontSize:12, lineHeight:1.65}}>{prlExample}</pre>
              </div>
            </div>
            <div>
              <div style={{fontSize:13, fontWeight:700, color:'var(--dgw-muted)', marginBottom:8, letterSpacing:'.4px', textTransform:'uppercase'}}>PQL Query — Interactive exploration</div>
              <div className="dgw-code-block">
                <div className="dgw-code-block-header">
                  <span>explore.pql</span>
                  <span style={{color:'#a78bfa'}}>PRISM Query Language</span>
                </div>
                <pre style={{fontSize:12, lineHeight:1.65}}>{vrlExample}</pre>
              </div>
            </div>
          </div>
          {/* Callout */}
          <div style={{
            padding:'16px 24px', borderRadius:12,
            background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.08)',
            display:'flex', alignItems:'center', gap:16,
          }}>
            <span style={{fontSize:22}}>💡</span>
            <div style={{fontSize:14, color:'var(--dgw-muted)', lineHeight:1.6}}>
              <strong style={{color:'var(--dgw-text)'}}>Unified dual-mode DSL.</strong>{' '}
              The <code style={{color: accentColor, background:'rgba(6,182,212,.1)', padding:'0 4px', borderRadius:3}}>find</code> clause
              is identical in Rule Mode and Query Mode.
              No knowledge transfer overhead — security engineers and developers use the same language.
            </div>
          </div>
        </div>

        {/* How PRISM compares (category-based) */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Positioning</span>
            <h2 style={{color:'var(--dgw-text)'}}>How PRISM compares</h2>
            <p className="dgw-section-lead">
              Every category of tool solves part of the problem. PRISM solves the whole thing from one graph.
            </p>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:16}}>
            {COMPARISON_CATEGORIES.map(cat => (
              <div key={cat.category} style={{
                display:'grid', gridTemplateColumns:'1fr 1fr', gap:0,
                borderRadius:14, overflow:'hidden',
                border:'1px solid rgba(255,255,255,.08)',
              }}>
                <div style={{padding:'24px 28px', background:'rgba(255,255,255,.02)'}}>
                  <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12}}>
                    <span style={{fontSize:20}}>{cat.icon}</span>
                    <span style={{fontSize:14, fontWeight:800, color:'var(--dgw-text)'}}>{cat.category}</span>
                  </div>
                  <p style={{fontSize:14, color:'#ef4444', lineHeight:1.6, margin:0, opacity:.8}}>{cat.gap}</p>
                </div>
                <div style={{padding:'24px 28px', background:`rgba(${accentRgb},.04)`, borderLeft:'1px solid rgba(255,255,255,.06)'}}>
                  <div style={{fontSize:12, fontWeight:700, color:accentColor, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:10}}>PRISM</div>
                  <p style={{fontSize:14, color:'var(--dgw-muted)', lineHeight:1.6, margin:0}}>{cat.prism}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Deep Dive */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Under the hood</span>
            <h2 style={{color:'var(--dgw-text)'}}>Technical deep dive</h2>
            <p className="dgw-section-lead">
              For teams that want to understand the internals before committing.
            </p>
          </div>
          <div style={{borderRadius:14, border:'1px solid rgba(255,255,255,.08)', overflow:'hidden'}}>
            <div style={{display:'flex', borderBottom:'1px solid rgba(255,255,255,.08)', flexWrap:'wrap'}}>
              {DEEP_DIVE_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding:'14px 24px', fontSize:13, fontWeight:700,
                    color: activeTab === tab.id ? accentColor : '#64748b',
                    background: activeTab === tab.id ? `rgba(${accentRgb},.08)` : 'transparent',
                    border:'none', borderBottom: activeTab === tab.id ? `2px solid ${accentColor}` : '2px solid transparent',
                    cursor:'pointer', transition:'all .2s',
                    letterSpacing:'.3px',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div style={{padding:'28px 32px'}}>
              {DEEP_DIVE_TABS.filter(t => t.id === activeTab).map(tab => (
                <p key={tab.id} style={{
                  fontSize:15, color:'var(--dgw-muted)', lineHeight:1.8, margin:0,
                  fontFamily:"'SF Mono','Fira Code',monospace", letterSpacing:'-.2px',
                }}>
                  {tab.content}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Benchmark comparison table */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Benchmarks</span>
            <h2 style={{color:'var(--dgw-text)'}}>Measured on real code. Not estimated.</h2>
            <p className="dgw-section-lead">Numbers from a 2023 MacBook Pro M2, 50K-line Go service. Same hardware, all tools.</p>
          </div>
          <div className="dgw-spec-table-wrap" style={{borderRadius:14, border:'1px solid rgba(255,255,255,.08)'}}>
            <table className="dgw-spec-table">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th style={{color: accentColor}}>PRISM</th>
                  <th>Pattern Matcher</th>
                  <th>Academic CPG</th>
                  <th>Enterprise SAST</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(row => (
                  <tr key={row.metric}>
                    <td style={{fontWeight:600}}>{row.metric}</td>
                    <td style={{color: accentColor, fontWeight:600}}>{row.prism}</td>
                    <td style={{color:'var(--dgw-muted)'}}>{row.semgrep}</td>
                    <td style={{color:'var(--dgw-muted)'}}>{row.joern}</td>
                    <td style={{color:'var(--dgw-muted)'}}>{row.codeql}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Commands reference */}
        <div style={{marginBottom:100}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>CLI reference</span>
            <h2 style={{color:'var(--dgw-text)'}}>18 commands. One binary.</h2>
            <p className="dgw-section-lead">Every analysis ships in the same <code style={{color:accentColor, background:`rgba(${accentRgb},.1)`, padding:'1px 5px', borderRadius:4, fontSize:13}}>26MB prism</code> binary.</p>
          </div>
          <div className="dgw-spec-table-wrap" style={{borderRadius:14, border:'1px solid rgba(255,255,255,.08)'}}>
            <table className="dgw-spec-table">
              <thead>
                <tr>
                  <th>Command</th>
                  <th>Purpose</th>
                  <th>Key flags</th>
                </tr>
              </thead>
              <tbody>
                {ALL_COMMANDS.map(c => (
                  <tr key={c.cmd}>
                    <td><code style={{color:accentColor, fontFamily:'monospace', fontSize:13}}>{c.cmd}</code></td>
                    <td style={{color:'var(--dgw-muted)'}}>{c.purpose}</td>
                    <td style={{color:'#4a607a', fontFamily:'monospace', fontSize:12}}>{c.highlight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Roadmap */}
        <div style={{marginBottom:80}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Roadmap</span>
            <h2 style={{color:'var(--dgw-text)'}}>Where the engine is going</h2>
            <p className="dgw-section-lead">
              From single-binary core engine to autonomous, zero-rule vulnerability prediction.
            </p>
          </div>
          <div style={{maxWidth:700, margin:'0 auto'}}>
            <div className="dgw-roadmap">
              {roadmap.map(step => (
                <div key={step.phase} className="dgw-roadmap-step">
                  <div className={`dgw-roadmap-bullet ${step.status}`}>
                    {step.status === 'done' ? '✓' : step.status === 'active' ? '●' : '○'}
                  </div>
                  <div className="dgw-roadmap-content">
                    <div className="dgw-roadmap-phase">{step.phase}</div>
                    <div className="dgw-roadmap-title">{step.title}</div>
                    <div className="dgw-roadmap-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Spec table */}
        <div style={{marginBottom:80}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Technical specs</span>
            <h2 style={{color:'var(--dgw-text)'}}>Measured. Not estimated.</h2>
            <p className="dgw-section-lead">MacBook Pro M2, 50K-line Go service. Same hardware, all tools.</p>
          </div>
          <div className="dgw-spec-table-wrap" style={{borderRadius:14, border:'1px solid rgba(255,255,255,.08)'}}>
            <table className="dgw-spec-table">
              <thead>
                <tr><th>Specification</th><th>Value</th></tr>
              </thead>
              <tbody>
                <tr><td>Cold start</td><td><strong>38ms</strong> measured</td></tr>
                <tr><td>Parse + build CPG</td><td><strong>120ms</strong> on 50K-line Go service</td></tr>
                <tr><td>Taint analysis (40 rules)</td><td><strong>85ms</strong></td></tr>
                <tr><td>Total scan time</td><td><strong>243ms</strong> end-to-end</td></tr>
                <tr><td>Peak memory</td><td><strong>42MB</strong></td></tr>
                <tr><td>Binary size</td><td><strong>26MB</strong></td></tr>
                <tr><td>Runtime dependencies</td><td><strong>Zero</strong> — pure Go static binary</td></tr>
                <tr><td>Supported languages</td><td><strong>9</strong> — Go, Java, Python, JavaScript, TypeScript, Kotlin, ABAP, COBOL, JCL</td></tr>
                <tr><td>Built-in rules</td><td><strong>179</strong> — Go (23) · Java (31) · Python (24) · JS (22) · TS (16) · Kotlin (24) · ABAP (20) · COBOL (15) · JCL (4)</td></tr>
                <tr><td>Rule severity</td><td><strong>52 critical · 76 high · 43 medium · 8 low</strong></td></tr>
                <tr><td>CPG layers</td><td><strong>5</strong> — AST, CFG, DDG, CDG, Call Graph</td></tr>
                <tr><td>Universal AST nodes</td><td><strong>58</strong> NodeKind constants</td></tr>
                <tr><td>Edge types</td><td><strong>12</strong></td></tr>
                <tr><td>Taint precision</td><td><strong>Inter-procedural + field-sensitive</strong></td></tr>
                <tr><td>Output formats</td><td><strong>SARIF 2.1.0</strong>, JSON, table, GitHub markdown</td></tr>
                <tr><td>Embedding model</td><td><strong>Go package</strong> — no subprocess, no FFI overhead</td></tr>
                <tr><td>License</td><td><strong>MIT</strong> — free for commercial and open-source use</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA */}
        <div style={{
          padding:56, borderRadius:20,
          background:`linear-gradient(135deg, rgba(${accentRgb},.1), rgba(6,182,212,.04))`,
          border:`1px solid rgba(${accentRgb},.2)`,
          textAlign:'center',
        }}>
          <h3 style={{fontSize:32, fontWeight:800, color:'var(--dgw-text)', marginBottom:12, letterSpacing:'-.5px'}}>38ms cold start. Zero dependencies. Ship it.</h3>
          <p style={{color:'var(--dgw-muted)', marginBottom:32, fontSize:16, maxWidth:520, margin:'0 auto 32px'}}>
            Use as a standalone CLI, embed as a Go package, or let Vigil and DepGuard
            use it as their shared analysis engine. MIT licensed.
          </p>
          <div style={{display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap'}}>
            <Link to="/trial?plan=professional" className="dgw-btn dgw-btn-prism dgw-btn-lg">Get started free →</Link>
            <Link to="/website/product/vigil" className="dgw-btn dgw-btn-ghost dgw-btn-lg">👁️ See Vigil</Link>
            <Link to="/website/product/depguard" className="dgw-btn dgw-btn-ghost dgw-btn-lg">🛡️ See DepGuard</Link>
          </div>
        </div>
      </div>

      {/* Nous — AI Reasoning Layer */}
      <section style={{ padding: '80px clamp(20px,5vw,60px)', background: '#04080f' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-block', background: 'rgba(188,140,255,.12)', border: '1px solid rgba(188,140,255,.3)', borderRadius: 100, padding: '4px 14px', fontSize: 11, fontWeight: 700, color: '#bc8cff', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
              AI Intelligence
            </span>
            <h2 style={{ fontSize: 'clamp(28px,4vw,44px)', fontWeight: 900, color: '#f0f6fc', marginBottom: 16, letterSpacing: '-0.5px' }}>
              Nous — Graph-Grounded Code Intelligence
            </h2>
            <p style={{ fontSize: 17, color: '#8b949e', maxWidth: 640, margin: '0 auto', lineHeight: 1.7 }}>
              Nous is the AI reasoning layer on top of PRISM's CPG. It doesn't guess about code.
              It traverses the actual graph — every answer cites CPG nodes, call paths, or taint flows.
              Six tools. Max 6 agentic iterations per question. Graph evidence, not hallucination.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 48 }}>
            {[
              { icon: '🧠', title: 'Graph-grounded answers', desc: 'Every Nous response cites actual CPG nodes, call paths, or taint flows. The graph is the evidence, not a prompt.' },
              { icon: '💬', title: 'Natural language queries', desc: '"Which HTTP handlers lack auth before a DB call?" — answered via CPG traversal with PQL, not a guess.' },
              { icon: '🔌', title: 'Multi-provider', desc: 'GitHub Copilot SDK · OpenAI · Anthropic · Ollama. Bring your own model. Nous provides the grounding layer.' },
              { icon: '🔧', title: '7 tools', desc: 'run_pql_query, run_pql_proof, run_compliance_suite, get_function_detail, search_sensitive_flows, get_dead_code — and counting.' },
            ].map(item => (
              <div key={item.title} style={{ background: 'rgba(188,140,255,.06)', border: '1px solid rgba(188,140,255,.2)', borderRadius: 12, padding: '24px 22px' }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{item.icon}</div>
                <div style={{ fontWeight: 700, color: '#e6edf3', marginBottom: 8 }}>{item.title}</div>
                <div style={{ fontSize: 14, color: '#8b949e', lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Decision Makers */}
      <section style={{ padding: '80px clamp(20px,5vw,60px)', background: '#0a0e17' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-block', background: `rgba(${accentRgb},.12)`, border: `1px solid rgba(${accentRgb},.3)`, borderRadius: 100, padding: '4px 14px', fontSize: 11, fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
              Outcomes
            </span>
            <h2 style={{ fontSize: 'clamp(28px,4vw,44px)', fontWeight: 900, color: '#f0f6fc', marginBottom: 16, letterSpacing: '-0.5px' }}>
              For decision makers
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
            {DECISION_MAKERS.map(d => (
              <div key={d.big} style={{
                padding: '36px 28px', borderRadius: 16, textAlign: 'center',
                background: `rgba(${accentRgb},.04)`,
                border: `1px solid rgba(${accentRgb},.15)`,
              }}>
                <div style={{ fontSize: 36, fontWeight: 900, color: accentColor, marginBottom: 16, letterSpacing: '-1px', lineHeight: 1.1 }}>{d.big}</div>
                <p style={{ fontSize: 15, color: '#8b949e', lineHeight: 1.7, margin: 0 }}>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book + Playground + Studio */}
      <section style={{ padding: '80px clamp(20px,5vw,60px)', background: '#0d1117' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          {/* Book */}
          <div style={{ background: 'rgba(6,182,212,.06)', border: '1px solid rgba(6,182,212,.2)', borderRadius: 16, padding: '36px 32px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>📖</div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0891b2', marginBottom: 8 }}>The Book</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: '#e6edf3', marginBottom: 12, lineHeight: 1.25 }}>The Inner Life of Software</h3>
            <p style={{ fontSize: 15, color: '#8b949e', lineHeight: 1.7, marginBottom: 24 }}>
              15 chapters on code intelligence: how CPGs work, PQL queries,
              security analysis patterns, Nous AI, and compliance.
              EPUB and hosted at book.lexint.io.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['CPG fundamentals', 'PQL queries', 'Security patterns', 'Nous AI', 'Compliance'].map(tag => (
                <span key={tag} style={{ background: 'rgba(6,182,212,.1)', border: '1px solid rgba(6,182,212,.25)', borderRadius: 100, padding: '3px 10px', fontSize: 12, color: '#0891b2', fontWeight: 600 }}>{tag}</span>
              ))}
            </div>
            <a
              href="https://book.lexint.io"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginTop: 24, display: 'inline-block', background: 'rgba(6,182,212,.12)', border: '1px solid rgba(6,182,212,.3)', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 700, color: '#06b6d4', textDecoration: 'none' }}
            >
              📖 Read at book.lexint.io →
            </a>
          </div>

          {/* Playground */}
          <div style={{ background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.2)', borderRadius: 16, padding: '36px 32px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🧪</div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6366f1', marginBottom: 8 }}>Interactive</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: '#e6edf3', marginBottom: 12, lineHeight: 1.25 }}>PRISM Playground</h3>
            <p style={{ fontSize: 15, color: '#8b949e', lineHeight: 1.7, marginBottom: 24 }}>
              PQL queries in your browser — no installation, no signup.
              5 built-in sample repos. 14-door PQL curriculum. SSE query streaming.
              Nous AI chat integrated.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
              {['Live PQL', 'CPG visualizer', '5 sample repos', 'Nous AI chat'].map(tag => (
                <span key={tag} style={{ background: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.25)', borderRadius: 100, padding: '3px 10px', fontSize: 12, color: '#818cf8', fontWeight: 600 }}>{tag}</span>
              ))}
            </div>
            <div style={{ display: 'inline-block', background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.3)', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 700, color: '#818cf8', fontFamily: 'monospace' }}>
              prism serve --playground
            </div>
          </div>

          {/* Studio */}
          <div style={{ background: 'rgba(16,185,129,.06)', border: '1px solid rgba(16,185,129,.2)', borderRadius: 16, padding: '36px 32px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🖥️</div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#10b981', marginBottom: 8 }}>Dashboard</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: '#e6edf3', marginBottom: 12, lineHeight: 1.25 }}>PRISM Studio</h3>
            <p style={{ fontSize: 15, color: '#8b949e', lineHeight: 1.7, marginBottom: 24 }}>
              All 10 analyses in parallel. Live SSE streaming. Ghost Logic, Bridges,
              and Missing Guards derived automatically. One command to see everything.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
              {['10 analyses', 'SSE streaming', 'Ghost Logic', 'Risk quadrants'].map(tag => (
                <span key={tag} style={{ background: 'rgba(16,185,129,.1)', border: '1px solid rgba(16,185,129,.25)', borderRadius: 100, padding: '3px 10px', fontSize: 12, color: '#10b981', fontWeight: 600 }}>{tag}</span>
              ))}
            </div>
            <div style={{ display: 'inline-block', background: 'rgba(16,185,129,.12)', border: '1px solid rgba(16,185,129,.3)', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>
              prism serve --studio
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
